"use client";

import { useCallback, useRef, useState } from "react";

/**
 * Claves i18n por status HTTP de la descarga. El proxy devuelve el envelope del
 * backend, pero su `message` es prosa y no una clave, así que el error se traduce
 * acá por status (mismo criterio que useFileUpload).
 */
const STATUS_ERROR_KEYS: Record<number, string> = {
  401: "userNotAuth",
  404: "certificateNotIssuedYet"
};

export interface CertificatePdfDownloadView {
  /**
   * Pide el PDF al proxy y lo baja con el nombre indicado. Devuelve la clave i18n
   * del error, o `null` si salió bien. Ignora llamadas con una descarga en vuelo.
   */
  download: (filename: string) => Promise<string | null>;
  /** Hay una descarga en curso: el botón lo usa para el spinner y para bloquearse. */
  downloading: boolean;
}

/**
 * @function useCertificatePdfDownload
 * @description Descarga el certificado en PDF por el proxy same-origin
 * `GET /api/enrollments/:id/certificate/pdf` (la cookie httpOnly de sesión viaja
 * sola; el browser no tiene los tokens que exige el backend).
 *
 * Va por `fetch` + Blob y no por un `<a href download>` porque generar el PDF
 * arranca un Chromium headless del lado del backend y tarda varios segundos: así
 * la vista sabe cuándo empieza y cuándo termina, puede mostrarlo y puede impedir
 * que el usuario dispare varias generaciones. De paso, un error deja de bajarse
 * como un archivo roto y se puede avisar.
 *
 * @returns {CertificatePdfDownloadView} `download` + estado de la descarga en curso.
 */
const useCertificatePdfDownload = (
  enrollmentId: string
): CertificatePdfDownloadView => {
  const [downloading, setDownloading] = useState(false);
  // Guard sincrónico contra dobles clicks: el estado `downloading` llega un
  // render tarde, el ref corta la re-entrada en el mismo tick.
  const inFlight = useRef(false);

  const download = useCallback(
    async (filename: string): Promise<string | null> => {
      if (inFlight.current) {
        return null;
      }

      inFlight.current = true;
      setDownloading(true);
      try {
        const response = await fetch(
          `/api/enrollments/${enrollmentId}/certificate/pdf`
        );
        if (!response.ok) {
          return (
            STATUS_ERROR_KEYS[response.status] ?? "certificateDownloadError"
          );
        }

        // Enlace sintético sobre el blob (mismo patrón que downloadCsv): el PDF ya
        // está en memoria, así que la descarga es instantánea y no vuelve a pegarle
        // al backend como haría un href al proxy.
        const url = URL.createObjectURL(await response.blob());
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        return null;
      } catch {
        return "certificateDownloadError";
      } finally {
        inFlight.current = false;
        setDownloading(false);
      }
    },
    [enrollmentId]
  );

  return { download, downloading };
};

export default useCertificatePdfDownload;
